import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { IFundSummaryStats } from 'src/components/molecules/fund-summary'
import type { ISimpleUpdatesItem } from 'src/components/molecules/simple-updates-list'

import DateAdapter from 'src/app/core/adapters/date/DateAdapter'
import { Toast, toastTypes } from 'src/app/core/services/toast'
import { useDocumentsQuery } from 'src/app/home/repositories/use-documents-query'
import { useFundUpdateDownloadQuery } from 'src/app/home/repositories/use-fund-update-download-query'
import { useFundUpdatesStatsQuery } from 'src/app/home/repositories/use-fund-updates-stats-query'
import { usePortfolioNewsQuery } from 'src/app/home/repositories/use-portfolio-news-query'

export interface IUseOverviewReturn {
  recentNewsItems: ISimpleUpdatesItem[]
  newDocumentItems: ISimpleUpdatesItem[]
  fundSummaryStats: IFundSummaryStats | undefined
  isFundSummaryLoading: boolean
  isLoading: boolean
  isDocumentsLoading: boolean
  selectedDocumentBlobUrl: string | null
  openDocumentPreview: (item: ISimpleUpdatesItem) => void
  closeDocumentPreview: () => void
}

export const useOverview = (): IUseOverviewReturn => {
  const { t } = useTranslation()
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState<string>('')
  const [selectedDocumentBlobUrl, setSelectedDocumentBlobUrl] = useState<
    string | null
  >(null)

  const { data, isLoading } = usePortfolioNewsQuery({
    page: 1,
    pageSize: 3,
    search: '',
    company: '',
    enabled: true,
    silentErrorToast: true,
  })

  const {
    data: statsData,
    isLoading: isFundSummaryLoading,
    isError: isStatsError,
  } = useFundUpdatesStatsQuery({ enabled: true, silentErrorToast: true })

  const {
    data: documentsData,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
  } = useDocumentsQuery({
    page: 1,
    pageSize: 3,
    enabled: true,
    silentErrorToast: true,
  })

  const downloadQuery = useFundUpdateDownloadQuery({
    documentUrl: selectedDocumentUrl,
    enabled: !!selectedDocumentUrl,
  })

  useEffect((): void => {
    if (!isStatsError && !isDocumentsError) {
      return
    }

    Toast.notify({
      type: toastTypes.ERROR,
      title: t('overview.loadErrorToastTitle'),
      message: t('overview.loadErrorToastMessage'),
      options: { toastId: 'overview-fetch-error' },
    })
  }, [isStatsError, isDocumentsError, t])

  useEffect((): (() => void) | void => {
    if (!downloadQuery.data || !selectedDocumentUrl) {
      return
    }
    const url = URL.createObjectURL(downloadQuery.data)
    setSelectedDocumentBlobUrl(url)
    return (): void => {
      URL.revokeObjectURL(url)
    }
  }, [downloadQuery.data, selectedDocumentUrl])

  const recentNewsItems: ISimpleUpdatesItem[] = (data?.items ?? []).map(
    article => ({
      id: article.id,
      title: article.articleName,
      relativeTime: article.publishedDisplay,
    }),
  )

  const newDocumentItems: ISimpleUpdatesItem[] = (
    documentsData?.items ?? []
  ).map(doc => ({
    id: doc.id,
    title: doc.documentTitle,
    relativeTime: `Added ${DateAdapter.formatToRelativeTime(doc.addedAt)}`,
    subtitle: `${doc.category} · ${doc.displayDate}`,
    pdfUrl: doc.documentUrl,
  }))

  const openDocumentPreview = (item: ISimpleUpdatesItem): void => {
    setSelectedDocumentBlobUrl(null)
    setSelectedDocumentUrl(item.pdfUrl ?? '')
  }

  const closeDocumentPreview = (): void => {
    setSelectedDocumentUrl('')
    setSelectedDocumentBlobUrl(null)
  }

  return {
    recentNewsItems,
    newDocumentItems,
    fundSummaryStats: statsData,
    isFundSummaryLoading,
    isLoading,
    isDocumentsLoading,
    selectedDocumentBlobUrl,
    openDocumentPreview,
    closeDocumentPreview,
  }
}
